/**
 * Utility functions for entity operations and UI helpers
 */

/**
 * Format date for display with timezone support
 * Returns format: "Jun 28, 2025, 22:36" in Jerusalem timezone
 */
export const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
        let date;
        
        // Check if it's RFC 2822 format (e.g., "Fri, 08 Aug 2025 17:49:36 GMT")
        if (dateString.includes('GMT') || dateString.includes('UTC') || /^[A-Za-z]{3}, \d{2} [A-Za-z]{3} \d{4}/.test(dateString)) {
            // Handle RFC 2822 format - can be parsed directly
            date = new Date(dateString);
        } else {
            // Handle ISO format - ensure UTC by appending 'Z' if not present
            const normalizedDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
            date = new Date(normalizedDateString);
        }

        // Check if date is valid
        if (isNaN(date.getTime())) {
            // Return original string if parsing fails (silent fallback)
            return dateString;
        }

        // Format to Jerusalem timezone (Asia/Jerusalem) with 24-hour time
        return date.toLocaleString('en-US', {
            timeZone: 'Asia/Jerusalem',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    } catch (error) {
        // Return original string on error (silent fallback)
        return dateString;
    }
};

/**
 * Convert local date (Jerusalem time) to GMT for server requests
 * Simplifies date filtering by sending exact dates as both fromdate and todate
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} - ISO format date string converted to global time
 * @private - Only used internally by convertToDateRangeFilter
 */
const convertLocalDateToGMT = (dateString) => {
    if (!dateString) return '';
    
    // Input is like "2025-06-25", create a date object at start of day in Jerusalem timezone
    // We need to explicitly handle the Jerusalem timezone conversion
    const localDate = new Date(dateString + 'T00:00:00');
    
    // Convert to global time and return in ISO format (YYYY-MM-DDTHH:MM:SS)
    // The server expects ISO format that is already converted to global time
    return localDate.toISOString().slice(0, 19); // Remove milliseconds and 'Z', keep YYYY-MM-DDTHH:MM:SS
};

/**
 * Convert date range for simple server filtering
 * @param {string} startDate - Start date string in YYYY-MM-DD format
 * @param {string} endDate - End date string in YYYY-MM-DD format
 * @returns {Object} - Object with fromdate and todate for date range filtering
 */
export const convertToDateRangeFilter = (startDate, endDate) => {
    const result = {};
    
    if (startDate) {
        result.fromdate = convertLocalDateToGMT(startDate);
    }
    
    if (endDate) {
        // For end date, we want to include the entire day, so we set it to end of day
        const endDateTime = new Date(endDate + 'T23:59:59');
        result.todate = endDateTime.toISOString().slice(0, 19);
    }
    
    return result;
};

/**
 * Human-readable label for an antisemitism type id from the API
 * (e.g. CW1 → "CyberWell 1", 3 → "IHRA 3").
 */
export const formatAntisemitismTypeLabel = (type) => {
    const s = String(type).trim();
    const cw = /^CW(\d+)$/i.exec(s);
    if (cw) return `CyberWell ${cw[1]}`;
    return `IHRA ${s}`;
};